import { BALANCE, BUILDINGS, CLOUD_LEVELS, OUTPOSTS } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import { capitalHex, techMods, type GameState, type Hex } from '@sd/sim';
import { clock, outpostName } from '../ui/format';

/**
 * Timers the player watches on the map (#96): a dust cloud being scanned,
 * a building or an outpost going up. Pure, so the map, the selection card
 * and the tests read the same numbers.
 */
export interface ProgressItem {
  /** Stable while the work lasts: the map reuses its label. */
  readonly id: string;
  readonly hex: Hex;
  /** 0..1 of the current step. */
  readonly value: number;
  readonly label: string;
  /** Time left, where the panel shows it (#115). */
  readonly left?: string;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Where a scan stands: the current find's share, and finds made of the cloud's total. */
export function scanProgress(
  state: GameState,
  fleetId: number,
): { value: number; done: number; total: number } | null {
  const fleet = state.fleets.find((f) => f.id === fleetId);
  if (!fleet?.scan) return null;
  const cloud = state.system.clouds.find((c) => c.id === fleet.scan!.cloudId);
  if (!cloud) return null;
  const owner = state.players.find((p) => p.id === fleet.owner);
  const scanTime = owner ? techMods(owner.techs).scanTime : 1;
  const ticks = Math.ceil(CLOUD_LEVELS[cloud.level].scanTicks * scanTime);
  const total = CLOUD_LEVELS[cloud.level].finds;
  const left = state.exploration.findsLeft[cloud.id] ?? 0;
  return { value: clamp01(fleet.scan.progress / ticks), done: total - left, total };
}

export function progressItems(state: GameState, me: string, locale: Locale): ProgressItem[] {
  const items: ProgressItem[] = [];
  for (const f of state.fleets) {
    if (f.owner !== me || !f.scan) continue;
    const scan = scanProgress(state, f.id);
    if (!scan) continue;
    items.push({
      id: `scan:${f.id}`,
      hex: f.hex,
      value: scan.value,
      label: t(locale, 'ui.progress.scan', { n: scan.done + 1, total: scan.total }),
    });
  }
  const player = state.players.find((p) => p.id === me);
  if (player) {
    const home = capitalHex(state, me);
    player.planet.buildings.forEach((b, i) => {
      if (!b.construction) return;
      const span = BUILDINGS[b.id].buildCycles * BALANCE.cycleTicks;
      items.push({
        id: `build:${i}:${b.construction.targetLevel}`,
        hex: home,
        value: clamp01(1 - (b.construction.readyTick - state.tick) / span),
        left: clock(Math.max(0, b.construction.readyTick - state.tick)),
        label: t(locale, 'ui.progress.build', {
          name: t(locale, `building.${b.id}.name`),
          level: b.construction.targetLevel,
        }),
      });
    });
  }
  for (const o of state.outposts) {
    if (o.owner !== me || o.readyTick <= state.tick) continue;
    const span = OUTPOSTS[o.kind].buildCycles * BALANCE.cycleTicks;
    items.push({
      id: `outpost:${o.id}`,
      hex: o.hex,
      value: clamp01(1 - (o.readyTick - state.tick) / span),
      label: outpostName(locale, o),
    });
  }
  return items;
}
